import React, {useEffect, useState} from 'react';
import {ProductType} from '@wix/wixstores-client-core';
import {TextButton, TextButtonPriority, Tooltip} from '@wix/wix-ui-tpa/cssVars';
import {useTranslation} from '@wix/yoshi-flow-editor';
import {CodeInput} from './CodeInput/CodeInput';
import {classes} from './Coupon.st.css';
import {Tag} from '@wix/wix-ui-icons-common';
import {useControllerProps} from '../../../domain/controllers/ControllerContext';
import {useSettingsWithDefaults} from '../../../hooks/useSettingsWithDefaults';
import type {ISettingsParam} from '@wix/tpa-settings';
import {CouponTag} from './CouponTag/CouponTag';

export enum CouponDataHook {
  CouponRoot = 'Coupon.Root',
  EnterACouponButton = 'Coupon.EnterACouponButton',
  EnterACouponButtonDisabledTooltip = 'Coupon.EnterACodeButtonDisabledTooltip',
  CouponInputRoot = 'Coupon.CouponInputRoot',
}

export interface CouponProps {
  textButtonSettingKey?: ISettingsParam;
  placeholderTranslationKey?: string;
  applyButtonTranslationKey?: string;
}

export const Coupon = ({textButtonSettingKey, placeholderTranslationKey, applyButtonTranslationKey}: CouponProps) => {
  const {t} = useTranslation();
  const {
    cartModel,
    isGiftCardMixedCartEnabled,
    sendToggleCouponBi,
    addCoupon,
    removeCoupon,
    couponError,
    isCouponUpdateInProgress,
    CouponAlignmentInCartAndCheckout,
  } = useControllerProps().cartStore;
  const appliedCoupon = cartModel.coupon;
  const isCouponApplied = !!appliedCoupon;
  const shouldDisableCouponForGiftCard =
    isGiftCardMixedCartEnabled && cartModel.lineItemsTypes.includes(ProductType.GIFT_CARD);
  const {getSettingsWithDefaults} = useSettingsWithDefaults();
  const [isCollapsed, setIsCollapsed] = useState(!isCouponApplied);

  useEffect(() => {
    if (!isCouponApplied) {
      return;
    }
    setIsCollapsed(!isCouponApplied);
  }, [isCouponApplied]);

  const onTextButtonClicked = () => {
    isCollapsed && sendToggleCouponBi();
    setIsCollapsed(!isCollapsed);
  };

  const handleAddCoupon = (code: string) => {
    return addCoupon(code);
  };

  const handleRemoveCoupon = () => {
    return removeCoupon();
  };

  const textButton = (
    <TextButton
      data-hook={CouponDataHook.EnterACouponButton}
      priority={TextButtonPriority.primary}
      className={classes.textButton}
      prefixIcon={<Tag />}
      aria-expanded={!isCollapsed}
      onClick={onTextButtonClicked}
      disabled={shouldDisableCouponForGiftCard}>
      {getSettingsWithDefaults(textButtonSettingKey)}
    </TextButton>
  );

  return (
    <div data-hook={CouponDataHook.CouponRoot} className={classes.couponRoot}>
      {shouldDisableCouponForGiftCard ? (
        <Tooltip
          data-hook={CouponDataHook.EnterACouponButtonDisabledTooltip}
          content={t('cart.errors.coupons.ERROR_CANT_PURCHASE_GC_MESSAGE')}
          minWidth={220}>
          {textButton}
        </Tooltip>
      ) : (
        textButton
      )}
      {!isCollapsed && (
        <div className={CouponAlignmentInCartAndCheckout ? classes.codeInputContainer : undefined}>
          <CodeInput
            dataHook={CouponDataHook.CouponInputRoot}
            applyText={t(applyButtonTranslationKey)}
            removeText={t('cart.remove_button')}
            appliedValue={appliedCoupon?.code}
            onApply={handleAddCoupon}
            onRemove={handleRemoveCoupon}
            error={couponError?.message}
            placeholder={t(placeholderTranslationKey)}
            isLoading={isCouponUpdateInProgress}
            ariaLabel={t('cart.coupon_input_code_aria_label')}
          />
          {CouponAlignmentInCartAndCheckout && <CouponTag />}
        </div>
      )}
    </div>
  );
};
